import React, { useMemo, useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, MessageCircle, ArrowRight, MapPin, Anchor, ZoomIn } from "lucide-react";
import { Link } from "react-router-dom";
import { APIProvider, Map, useMap } from "@vis.gl/react-google-maps";
import { useLanguage } from "@/context/LanguageContext";
import { networkLocations, networkCategories, istanbulHub } from "@/data/networkLocations";
import { contactInfo } from "@/i18n/translations";

// Türkiye-centric viewport
const DEFAULT_CENTER = { lat: 39.0, lng: 34.0 };
const DEFAULT_ZOOM = 6;
const TURKEY_BOUNDS = { north: 44.0, south: 33.5, west: 22.0, east: 47.5 };

// Zoom threshold below which İstanbul markers collapse into a single hub
const ISTANBUL_CLUSTER_ZOOM = 9;

// ---------- Premium "Aegean Mist" map style ----------
const AEGEAN_MAP_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#EFE9DD" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#5A6B7A" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#FFFFFF" }, { weight: 2 }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#C7BFA8" }, { weight: 0.6 }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#0B1E35" }, { weight: 1.2 }] },
  { featureType: "administrative.country", elementType: "labels.text.fill", stylers: [{ color: "#0B1E35" }] },
  { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#0B1E35" }] },
  { featureType: "administrative.neighborhood", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.province", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#EFE9DD" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#EFE9DD" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#DDE5D3" }] },
  { featureType: "road", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#BCD6DD" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#5A8B95" }] },
  { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#FFFFFF" }, { weight: 2 }] },
];

// ---------- Filters ----------
const buildFilters = (lang) => [
  { id: "all", label: lang === "ru" ? "Все локации" : lang === "tr" ? "Tüm Lokasyonlar" : "All Locations" },
  { id: "blacksea", label: lang === "ru" ? "Чёрное море" : lang === "tr" ? "Karadeniz" : "Black Sea Ports" },
  { id: "marmara", label: lang === "ru" ? "Мармара" : lang === "tr" ? "Marmara" : "Marmara" },
  { id: "aegean", label: lang === "ru" ? "Эгейское море" : lang === "tr" ? "Ege Marinaları" : "Aegean Marinas" },
  { id: "mediterranean", label: lang === "ru" ? "Средиземное море" : lang === "tr" ? "Akdeniz Marinaları" : "Mediterranean Marinas" },
  { id: "shipyard", label: lang === "ru" ? "Верфь · Техническая" : lang === "tr" ? "Tersane / Teknik" : "Technical / Shipyard", tagFilter: true },
  { id: "superyacht", label: lang === "ru" ? "Для суперъяхт" : lang === "tr" ? "Süperyat Uyumlu" : "Superyacht Friendly", tagFilter: true },
  { id: "customs", label: lang === "ru" ? "Таможня" : lang === "tr" ? "Gümrük / Çıkış" : "Customs / Clearance", tagFilter: true },
  { id: "concierge", label: lang === "ru" ? "Консьерж / VIP" : lang === "tr" ? "Concierge / VIP" : "Concierge & VIP", tagFilter: true },
];

// ---------- Fixed map viewports per region ----------
// Whole region is shown (not just the markers' bounding box)
const REGION_VIEWPORT = {
  all: { center: { lat: 39.0, lng: 34.0 }, zoom: 6 },
  blacksea: { center: { lat: 41.7, lng: 35.5 }, zoom: 7 },
  marmara: { center: { lat: 40.6, lng: 28.5 }, zoom: 7 },
  aegean: { center: { lat: 37.6, lng: 27.5 }, zoom: 7 },
  mediterranean: { center: { lat: 36.7, lng: 31.5 }, zoom: 7 },
};

// ---------- Rota logo PNG marker (all tiers share the same logo) ----------
const buildRotaIcon = (focused) => {
  const size = focused ? 44 : 32;
  return {
    url: "/rota-marker.png",
    scaledSize: { width: size, height: size },
    anchor: { x: size / 2, y: size / 2 },
  };
};

// ---------- Imperative marker (legacy google.maps.Marker) ----------
const RotaMarker = ({ location, active, selected, onClick }) => {
  const map = useMap();
  const isFocused = active || selected;

  useEffect(() => {
    if (!map || !window.google) return;
    const Size = window.google.maps.Size;
    const Point = window.google.maps.Point;
    const iconCfg = buildRotaIcon(isFocused);
    const marker = new window.google.maps.Marker({
      position: location.position,
      map,
      title: location.name,
      optimized: false,
      icon: {
        url: iconCfg.url,
        scaledSize: new Size(iconCfg.scaledSize.width, iconCfg.scaledSize.height),
        anchor: new Point(iconCfg.anchor.x, iconCfg.anchor.y),
      },
      zIndex: isFocused ? 1000 : 50,
      label: isFocused
        ? { text: location.name, color: "#0B1E35", fontFamily: "Playfair Display, serif", fontSize: "13px", fontWeight: "600", className: "rota-marker-label" }
        : null,
    });
    const listener = marker.addListener("click", () => onClick(location));
    return () => {
      window.google?.maps?.event?.removeListener(listener);
      marker.setMap(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, isFocused, location.id]);

  return null;
};

// ---------- Map controller (region viewport or manual centre + zoom) ----------
const MapController = ({ visible, filter, onZoomChange }) => {
  const map = useMap();
  const lastFilterRef = useRef("");

  useEffect(() => {
    if (!map || !window.google) return;
    const listener = map.addListener("zoom_changed", () => onZoomChange(map.getZoom()));
    onZoomChange(map.getZoom() || DEFAULT_ZOOM);
    return () => window.google?.maps?.event?.removeListener(listener);
  }, [map, onZoomChange]);

  useEffect(() => {
    if (!map || !window.google) return;
    if (lastFilterRef.current === filter) return;
    lastFilterRef.current = filter;

    // Fixed regional viewport for category filters (shows the whole region, not just markers)
    if (REGION_VIEWPORT[filter]) {
      map.setZoom(REGION_VIEWPORT[filter].zoom);
      map.setCenter(REGION_VIEWPORT[filter].center);
      return;
    }

    // Tag filters: fit to visible markers
    if (visible.length === 0) return;
    if (visible.length === 1) {
      map.setCenter(visible[0].position);
      map.setZoom(10);
      return;
    }
    const lats = visible.map((l) => l.position.lat);
    const lngs = visible.map((l) => l.position.lng);
    const center = {
      lat: (Math.min(...lats) + Math.max(...lats)) / 2,
      lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
    };
    const span = Math.max(
      Math.max(...lats) - Math.min(...lats),
      (Math.max(...lngs) - Math.min(...lngs)) * 0.6
    );
    let z = 6;
    if (span < 0.5) z = 10;
    else if (span < 1.2) z = 9;
    else if (span < 2.5) z = 8;
    else if (span < 5) z = 7;
    else if (span < 10) z = 6;
    else z = 5;
    map.setZoom(z);
    map.setCenter(center);
  }, [map, filter, visible]);

  return null;
};

// ---------- Side panel ----------
const LocationPanel = ({ location, onClose, lang }) => {
  if (!location) return null;
  const cat = networkCategories[location.category];
  return (
    <AnimatePresence>
      <motion.aside
        key={location.id}
        initial={{ x: 60, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 60, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="absolute top-0 right-0 bottom-0 w-full md:w-[420px] bg-white border-l border-navy/10 shadow-[0_0_40px_rgba(11,30,53,0.12)] z-20 overflow-y-auto"
        data-testid={`panel-${location.id}`}
      >
        <div className="sticky top-0 bg-white border-b border-navy/10 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
            <span className="overline">{cat[lang] || cat.en}</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-navy/50 hover:text-brand transition-colors" aria-label="Close" data-testid="panel-close-btn">
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-6">
          <h3 className="font-display text-3xl text-navy leading-tight" data-testid="panel-name">{location.name}</h3>
          <div className="mt-2 flex items-center gap-2 text-sm text-navy/60">
            <MapPin size={14} /><span>{location.region[lang] || location.region.en}</span>
          </div>
          <div className="mt-1 text-xs tracking-[0.15em] uppercase text-brand font-medium">{location.type[lang] || location.type.en}</div>
          <div className="mt-6 pb-6 border-b border-navy/10">
            <p className="text-navy/80 text-sm leading-relaxed">{location.description[lang] || location.description.en}</p>
          </div>
          <div className="mt-6">
            <div className="overline mb-3">{lang === "ru" ? "Услуги агентства" : lang === "tr" ? "Acente Hizmetleri" : "Agency Services"}</div>
            <ul className="space-y-2">
              {(location.services[lang] || location.services.en).map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-navy/80">
                  <span className="w-1 h-1 rounded-full bg-brand mt-2 shrink-0" /><span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-6 pt-6 border-t border-navy/10">
            <div className="overline mb-2">{lang === "ru" ? "Сценарий" : lang === "tr" ? "Kullanım Senaryosu" : "Use case"}</div>
            <p className="text-sm italic text-navy/70 leading-relaxed">{location.useCase[lang] || location.useCase.en}</p>
          </div>
          <div className="mt-8 space-y-3">
            <Link to="/contact" state={{ location: location.name }} className="block text-center bg-brand text-white py-3.5 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-brand-deep transition-colors" data-testid="panel-cta-request">
              {lang === "ru" ? "Запросить поддержку" : lang === "tr" ? "Destek Talep Et" : "Request Assistance"}
            </Link>
            <a href={`https://wa.me/${contactInfo.whatsapp}?text=${encodeURIComponent(`Hello Rota Yachting — inquiry about ${location.name}`)}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 border border-[#25D366] text-[#25D366] py-3 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-[#25D366] hover:text-white transition-colors" data-testid="panel-whatsapp">
              <MessageCircle size={14} /> WhatsApp
            </a>
          </div>
        </div>
      </motion.aside>
    </AnimatePresence>
  );
};

// ---------- Main ----------
const NetworkMap = () => {
  const { lang } = useLanguage();
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  const mapRef = useRef(null);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const filters = useMemo(() => buildFilters(lang), [lang]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matched = networkLocations.filter((loc) => {
      if (filter !== "all") {
        const activeFilter = filters.find((f) => f.id === filter);
        if (activeFilter?.tagFilter) {
          if (!loc.tags.includes(filter)) return false;
        } else if (loc.category !== filter) {
          return false;
        }
      }
      if (q) {
        const hay = [
          loc.name,
          loc.region[lang] || loc.region.en,
          loc.type[lang] || loc.type.en,
          loc.description[lang] || loc.description.en,
          ...((loc.services[lang] || loc.services.en) || []),
        ].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    // When showing all locations, pin Sinop (company HQ) to the top of the list.
    if (filter === "all") {
      return [...matched].sort((a, b) => {
        if (a.id === "sinop") return -1;
        if (b.id === "sinop") return 1;
        return 0;
      });
    }
    return matched;
  }, [filter, query, lang, filters]);

  const istanbulVisible = useMemo(() => visible.filter((l) => l.tags?.includes("istanbul-hub")), [visible]);
  const otherVisible = useMemo(() => visible.filter((l) => !l.tags?.includes("istanbul-hub")), [visible]);

  // İstanbul markers — visible when zoomed in OR when the Marmara filter is active
  const showIstanbulMarkers =
    mapZoom >= ISTANBUL_CLUSTER_ZOOM || filter === "marmara";

  const handleHubClick = () => {
    const m = mapRef.current;
    if (!m || !window.google) return;
    m.setZoom(istanbulHub.zoom);
    m.setCenter(istanbulHub.position);
  };

  const handleListClick = (loc) => {
    setSelected(loc);
    setHovered(loc.id);
    setTimeout(() => setHovered(null), 2200);
  };

  return (
    <section className="relative py-20 sm:py-24 px-6 sm:px-10 lg:px-16" data-testid="network-map-section" id="yacht-network">
      {/* Header */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
        <div className="lg:col-span-6">
          <div className="overline mb-4">{lang === "ru" ? "Сеть агентирования" : lang === "tr" ? "Acentelik Ağı" : "Service Network"}</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05]">
            {lang === "ru" ? (<>Охват яхтенного агентирования по<br /><em className="text-brand not-italic">всей Турции</em></>)
              : lang === "tr" ? (<>Türkiye Geneli Yat<br /><em className="text-brand not-italic">Acentelik Kapsamı</em></>)
              : (<>Yachting Agency Coverage<br /><em className="text-brand not-italic">Across Türkiye</em></>)}
          </h2>
        </div>
        <div className="lg:col-span-5 lg:col-start-8 flex items-end">
          <p className="text-navy/70 text-base leading-relaxed">
            {lang === "ru" ? "От Чёрного моря до Эгейского и Средиземного — Rota Yachting Agency обеспечивает бесшовное портовое, маринное, таможенное, логистическое и консьерж-сопровождение для яхт, посещающих Турцию."
              : lang === "tr" ? "Karadeniz'den Ege'ye ve Akdeniz'e — Rota Yachting Agency, Türkiye'yi ziyaret eden yatlar için kusursuz liman, marina, gümrük, lojistik ve concierge desteği sağlar."
              : "From the Black Sea to the Aegean and Mediterranean, Rota Yachting Agency provides seamless port, marina, customs, logistics and concierge support for yachts visiting Türkiye."}
          </p>
        </div>
      </div>

      {/* İstanbul Yacht Hub highlighted info card */}
      <div className="mb-10 grid grid-cols-1 lg:grid-cols-12 gap-6 border border-brand/30 bg-gradient-to-br from-navy via-navy to-[#0F2A4A] p-8 sm:p-10 text-white" data-testid="istanbul-hub-card">
        <div className="lg:col-span-8">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-2 h-2 rounded-full bg-brand-gold" style={{ backgroundColor: "#C9A24A" }} />
            <span className="text-[10px] tracking-[0.3em] uppercase" style={{ color: "#C9A24A" }}>
              {lang === "ru" ? "Стамбул · Яхтенный хаб" : lang === "tr" ? "İstanbul · Yat Merkezi" : "İstanbul · Yacht Hub"}
            </span>
          </div>
          <h3 className="font-display text-3xl sm:text-4xl leading-tight">
            {lang === "ru" ? "Стамбул — морские ворота Турции" : lang === "tr" ? "İstanbul — Türkiye'nin denizcilik kapısı" : "İstanbul — Türkiye's maritime gateway"}
          </h3>
          <p className="mt-4 text-white/75 text-sm sm:text-base leading-relaxed max-w-2xl">
            {lang === "ru"
              ? "Стамбул — ключевые морские ворота, соединяющие маршруты Чёрного моря, Мармары и Средиземноморья. Rota Yachting Agency предоставляет полный спектр поддержки яхт: координацию транзита Босфора, резервирование мест в маринах, таможенное оформление, снабжение, технические сервисы и VIP-консьерж."
              : lang === "tr"
              ? "İstanbul, Karadeniz, Marmara ve Akdeniz rotalarını birbirine bağlayan kilit denizcilik kapısıdır. Rota Yachting Agency; Boğaz transit koordinasyonu, marina rezervasyonları, gümrük çıkışı, ikmal, teknik servisler ve VIP concierge dahil eksiksiz yat desteği sağlar."
              : "İstanbul is the key maritime gateway connecting the Black Sea, Marmara and Mediterranean routes. Rota Yachting Agency provides full-service yacht support including Bosphorus transit coordination, marina reservations, customs clearance, provisioning, technical services and VIP concierge."}
          </p>
        </div>
        <div className="lg:col-span-4 flex flex-col justify-center gap-3">
          <button
            type="button"
            onClick={() => { setFilter("marmara"); handleHubClick(); }}
            className="text-[11px] tracking-[0.25em] uppercase font-semibold py-3.5 px-5 transition-colors"
            style={{ backgroundColor: "#C9A24A", color: "#0B1E35" }}
            data-testid="istanbul-hub-filter-btn"
          >
            {lang === "ru" ? "Приблизить к Стамбулу" : lang === "tr" ? "İstanbul'a Yakınlaş" : "Zoom into İstanbul"}
          </button>
          <Link
            to="/contact"
            state={{ location: "İstanbul Yacht Hub" }}
            className="text-center text-[11px] tracking-[0.25em] uppercase font-semibold py-3 border border-white/30 hover:border-white text-white transition-colors"
            data-testid="istanbul-hub-cta"
          >
            {lang === "ru" ? "Запросить поддержку в Стамбуле" : lang === "tr" ? "İstanbul Yat Desteği Talep Et" : "Request İstanbul Yacht Support"}
          </Link>
        </div>
      </div>

      {/* Filters + search */}
      <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between mb-6">
        <div className="flex flex-wrap gap-2" data-testid="network-filters">
          {filters.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 text-[11px] tracking-[0.15em] uppercase font-medium border transition-colors ${
                filter === f.id ? "bg-navy text-white border-navy" : "bg-white/60 text-navy/70 border-navy/15 hover:border-brand hover:text-brand"
              }`}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative w-full lg:w-72 shrink-0">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={lang === "ru" ? "Поиск порта или марины" : lang === "tr" ? "Liman veya marina ara" : "Search port or marina"}
            className="w-full bg-white/80 border border-navy/15 pl-9 pr-3 py-2.5 text-sm text-navy placeholder:text-navy/40 focus:outline-none focus:border-brand transition-colors"
            data-testid="network-search"
          />
        </div>
      </div>

      {/* Map + list */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 relative border border-navy/10 bg-white/40 backdrop-blur-sm overflow-hidden h-[560px]">
          {!apiKey ? (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-navy/60 px-6 text-center">
              Google Maps API key not configured.
            </div>
          ) : (
            <APIProvider apiKey={apiKey}>
              <Map
                mapId={process.env.REACT_APP_GOOGLE_MAPS_MAP_ID}
                defaultCenter={DEFAULT_CENTER}
                defaultZoom={DEFAULT_ZOOM}
                minZoom={5}
                maxZoom={16}
                gestureHandling="greedy"
                mapTypeControl={false}
                streetViewControl={false}
                fullscreenControl={false}
                zoomControl={true}
                clickableIcons={false}
                restriction={{ latLngBounds: TURKEY_BOUNDS, strictBounds: false }}
                style={{ width: "100%", height: "100%" }}
                onIdle={(e) => { mapRef.current = e.map; }}
                data-testid="google-map"
              >
                <MapController visible={visible} filter={filter} onZoomChange={setMapZoom} />
                {/* Non-İstanbul markers */}
                {otherVisible.map((loc) => (
                  <RotaMarker key={loc.id} location={loc} active={hovered === loc.id} selected={selected?.id === loc.id} onClick={(l) => setSelected(l)} />
                ))}
                {/* İstanbul markers — hidden when zoomed out */}
                {showIstanbulMarkers &&
                  istanbulVisible.map((loc) => (
                    <RotaMarker key={loc.id} location={loc} active={hovered === loc.id} selected={selected?.id === loc.id} onClick={(l) => setSelected(l)} />
                  ))}
              </Map>
            </APIProvider>
          )}

          {/* Microcopy hint when İstanbul markers hidden */}
          {!showIstanbulMarkers && istanbulVisible.length > 0 && (
            <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur-sm border border-navy/10 px-3 py-2 flex items-center gap-2 text-[10px] tracking-[0.15em] uppercase text-navy/70 shadow-sm" data-testid="zoom-hint">
              <ZoomIn size={12} className="text-brand" />
              <span>
                {lang === "ru" ? "Приблизьте, чтобы увидеть марины Стамбула" : lang === "tr" ? "İstanbul marina ağını görmek için yakınlaştırın" : "Zoom into İstanbul to explore marina network"}
              </span>
            </div>
          )}

          <LocationPanel location={selected} onClose={() => setSelected(null)} lang={lang} />

          <style>{`
            .rota-marker-label {
              padding: 2px 8px;
              background: rgba(255,255,255,0.92);
              border: 1px solid rgba(11,30,53,0.12);
              border-radius: 2px;
              transform: translateY(28px);
              letter-spacing: 0.02em;
              white-space: nowrap;
              box-shadow: 0 2px 8px rgba(11,30,53,0.12);
            }
          `}</style>
        </div>

        {/* Location list */}
        <div className="lg:col-span-4 border border-navy/10 bg-white/60 backdrop-blur-sm max-h-[560px] overflow-y-auto">
          <div className="px-4 py-3 border-b border-navy/10 sticky top-0 bg-white/80 backdrop-blur">
            <div className="overline">{visible.length} {lang === "ru" ? "локаций" : lang === "tr" ? "lokasyon" : "locations"}</div>
          </div>
          {visible.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-navy/50">{lang === "ru" ? "Ничего не найдено" : lang === "tr" ? "Sonuç bulunamadı" : "No results"}</div>
          ) : (
            <ul>
              {visible.map((loc) => {
                const cat = networkCategories[loc.category];
                const isActive = selected?.id === loc.id;
                return (
                  <li key={loc.id}>
                    <button
                      type="button"
                      onClick={() => handleListClick(loc)}
                      onMouseEnter={() => setHovered(loc.id)}
                      onMouseLeave={() => setHovered(null)}
                      className={`w-full text-left px-4 py-4 border-b border-navy/5 transition-colors ${isActive ? "bg-brand/5" : "hover:bg-navy/5"}`}
                      data-testid={`list-item-${loc.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 rounded-full mt-2 shrink-0" style={{ backgroundColor: cat.color }} />
                        <div className="flex-1 min-w-0">
                          <div className="font-display text-lg text-navy leading-tight truncate">{loc.name}</div>
                          <div className="text-[10px] tracking-[0.2em] uppercase text-navy/50 mt-1">{loc.region[lang] || loc.region.en}</div>
                        </div>
                        <ArrowRight size={14} className={`shrink-0 mt-2 ${isActive ? "text-brand" : "text-navy/30"}`} />
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="mt-12 border border-navy/10 bg-white/60 backdrop-blur-sm p-8 sm:p-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-3">
              <Anchor size={18} className="text-brand" strokeWidth={1.5} />
              <span className="overline">{lang === "ru" ? "Спланировать заход" : lang === "tr" ? "Yat Yanaşmanızı Planlayın" : "Plan Your Yacht Call"}</span>
            </div>
            <p className="font-display text-2xl sm:text-3xl text-navy leading-snug">
              {lang === "ru" ? (<>Ваша яхта прибывает в Трабзон, транзитирует Стамбул, идёт Эгейским морем или швартуется в Средиземном — <em className="text-brand not-italic">Rota Yachting Agency координирует каждую деталь. Beyond the Ordinary.</em></>)
                : lang === "tr" ? (<>Yatınız ister Trabzon'a varıyor, ister İstanbul'dan transit geçiyor, ister Ege'de seyrediyor ya da Akdeniz'de yanaşıyor olsun — <em className="text-brand not-italic">Rota Yachting Agency her detayı koordine etmeye hazır. Beyond the Ordinary.</em></>)
                : (<>Whether your yacht is arriving in Trabzon, transiting İstanbul, cruising the Aegean or berthing in the Mediterranean — <em className="text-brand not-italic">Rota Yachting Agency is ready to coordinate every detail. Beyond the Ordinary.</em></>)}
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link to="/contact" className="block text-center bg-brand text-white py-4 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-brand-deep transition-colors" data-testid="network-cta-contact">
              {lang === "ru" ? "Связаться с агентством" : lang === "tr" ? "Acente ile İletişime Geç" : "Contact Rota Yachting Agency"}
            </Link>
            <a href={`https://wa.me/${contactInfo.whatsapp}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 border border-navy/20 text-navy/80 py-3 text-[11px] tracking-[0.25em] uppercase font-semibold hover:border-[#25D366] hover:text-[#25D366] transition-colors" data-testid="network-cta-whatsapp">
              <MessageCircle size={14} /> WhatsApp Quick Contact
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NetworkMap;
